<template>
  <PForm
    ref="pForm"
    :heading="formHeading"
    :primary-cta="submitBtn"
    :secondary-cta="resetButton"
    :sub-heading="formSubHeading"
    @close="closeForm"
    @reset="onReset"
    @submit.prevent="onSubmit"
  >
    <template #header-info>
      <PBadge
        class="text-capitalize q-mx-md"
        :label="formData.status"
        :type="formData.status"
      />
    </template>

    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div v-if="!qualityCheckFormState.view" class="col">
          <PSelectInput
            v-model="formData.invc_id"
            :loading="validatingNumber"
            :options="invoicesWithActivePODs"
            :rules="[validateNumber]"
            :validations="{
              label: 'Invoice',
            }"
            input-class="ellipsis"
            label="Select an Invoice"
            option-label="number"
            option-value="_id"
            placeholder="Select invoice"
            required
            @filter="invoiceFilter.onFilter"
            @filter-abort="invoiceFilter.onFilterAbort"
            @update:model-value="populateFormData"
          />
        </div>
        <div class="col">
          <PInput
            v-model.trim="formData.number"
            :disable="qualityCheckFormState.view"
            label="QC No."
            placeholder="Enter value"
            required
          />
        </div>
        <div class="col">
          <PVDateInput
            v-model="formData.date"
            :disable="qualityCheckFormState.view"
            label="QC Date"
            placeholder="DD-MM-YYYY"
            required
          />
        </div>
        <div class="col">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            required
            disable
          />
        </div>
        <div v-if="qualityCheckFormState.view" class="col-12">
          <p v-if="formData?.qc_file?.length" class="q-mb-xs">QC File:</p>
          <PMap v-if="formData?.qc_file?.length" :files="formData?.qc_file" />
        </div>
      </div>
    </div>
    <q-separator v-if="!qualityCheckFormState.view" class="q-mb-xl q-mt-sm" />
    <div
      v-if="!qualityCheckFormState.view"
      class="border-none q-ma-none q-pa-none"
    >
      <PField
        v-model="formData.status_cd"
        label="Are there any returned / damaged products?"
      >
        <template #control>
          <POptionGroup
            v-model="formData.status_cd"
            :options="[
              {
                description: 'All well. Passed',
                label: 'Passed',
                value: 'passed',
              },
              {
                description: 'Partially Rejected case',
                label: 'Partially Rejected',
                value: 'partially_rejected',
              },
              {
                description: 'Fully Rejected / CN case',
                label: 'Rejected',
                value: 'rejected',
              },
            ]"
            @update:model-value="onUpdateStatus"
          />
        </template>
      </PField>
    </div>
    <q-separator
      v-if="isRejected && formData.invc_id"
      class="q-mb-xl q-mt-sm"
    />
    <div v-if="isRejected && formData.invc_id" class="overflow-hidden q-mt-lg">
      <legend class="q-mb-sm text-neutral text-weight-medium">
        Returned/Rejected Items ({{ formData.lineitems.length }})
      </legend>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData.lineitems.length"
        :pagination="pagination"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :columns="productListColumns"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        table-header-class="text-neutral-70 bg-neutral-10"
        title-class="text-neutral text-md"
        dense
        flat
        wrap-cells
      >
        <template #body-cell="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable
              :model-value="props.value"
              :type="props.col.type"
              disable
            />
          </q-td>
        </template>

        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <ItemNameCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-rate="props">
          <q-td :key="props.col.name" :props="props">
            <ItemRateCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-amount="props">
          <q-td :key="props.col.name" :props="props">
            <ItemAmountCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-qty="props">
          <q-td :key="props.col.name" :props="props">
            <ItemQuantityCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-action="props">
          <q-td :props="props">
            <div class="flex no-wrap justify-center">
              <PBtn
                :disable="disableLineItemActionButtons"
                color="primary"
                dense
                flat
                fluid
                rounded
                title="Edit Item"
                @click="
                  editItem({
                    item: props.row,
                    index: props.rowIndex,
                    entityType: 'Quality Check',
                  })
                "
              >
                <EditIcon class="icon--sm" />
              </PBtn>
              <PBtn
                :disable="disableLineItemActionButtons"
                dense
                flat
                rounded
                title="Delete Item"
              >
                <RemoveIcon
                  class="icon--sm"
                  @click="removeProduct(props.rowIndex)"
                />
              </PBtn>
            </div>
          </q-td>
        </template>
      </PTable>
      <p v-else class="q-my-xl text-neutral-50">
        <span class="block q-mb-xs">No Items</span>
        <span class="text-error-60">
          <q-icon class="icon--sm" name="sym_r_error_outline" />
          At least one item is required.
        </span>
      </p>
      <div v-if="showAddItemButton" class="flex">
        <PBtn
          class="q-mt-md q-px-xs"
          dense
          outline
          @click="
            addItem({
              entityType: 'Quality Check',
              items: invoiceLineItems,
            })
          "
        >
          <AddIcon class="icon--sm q-mr-sm" />
          <span>Add rejected items to QC</span>
        </PBtn>
      </div>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div
          :class="[
            !hasCreditNotesDebitNotes && qualityCheckFormState.view
              ? 'col-12'
              : 'col-6',
          ]"
        >
          <PInput
            v-model="formData.remarks"
            :disable="qualityCheckFormState.view"
            label="Remarks (if any)"
            placeholder="Enter remarks"
            type="textarea"
            autogrow
            required
          />
        </div>
        <div v-if="hasCreditNotesDebitNotes" class="col-6">
          <div class="row q-col-gutter-lg">
            <div class="col-6 ellipsis">
              <p
                class="text-neutral q-mb-none ellipsis"
                title="Total Rejected Amount"
              >
                Total Rejected Amount
              </p>
              <span class="text-xs text-neutral-70">Excludes Tax</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount
                      ? getTotalAmountWithoutTax()
                      : 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6">
              <span class="text-neutral"> Total Rejected Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div v-if="formData.tcs?.code" class="row q-mt-lg">
            <div class="col-6 ellipsis">
              <span class="text-neutral" title="TCS">
                TCS ({{ formData.tcs.percentage }}%)
                <sup>*</sup>
              </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount ? formData.tcs.amount : 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
          <div class="row q-mt-lg">
            <div class="col-6 ellipsis">
              <p
                class="text-neutral q-mb-none ellipsis"
                title="Grand Total Rejected"
              >
                Grand Total Rejected
              </p>
              <span class="text-xs text-neutral-70">Includes Tax</span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-separator v-if="hasCreditNotesDebitNotes" spaced="xl" />
    <div v-if="qualityCheckFormState.view">
      <span
        v-for="({ key, rowLabel, cta }, rowIndex) in associatedCreditDebitNotes"
        :key="rowIndex"
      >
        <q-separator v-if="rowIndex > 0" class="q-my-xl" />
        <p v-if="formData[key]?.length">
          Associated {{ rowLabel }} ({{ formData[key]?.length }})
        </p>

        <q-separator v-if="formData[key]?.length" />

        <PTable
          v-if="formData[key]?.length"
          :columns="creditNotesColumns"
          :rows="formData[key]"
          expandable-rows
          hide-bottom
          dense
        >
          <template #row="props">
            <q-td v-for="col in props.cols" :key="col.name" :props="props">
              <span v-if="col.name === 'number'">
                <PBtn
                  class="text-xs"
                  dense
                  flat
                  fluid
                  text
                  @click="cta(props.row)"
                >
                  {{ col.value || "View Details" }}
                </PBtn>
              </span>

              <PBadge
                v-else-if="col.name === 'status'"
                :label="col.value"
                :type="col.value"
              />

              <PBadge
                v-else-if="col.name === 'stage'"
                :label="col.value"
                :type="col.value"
              />

              <PPopover v-else-if="col.name === 'remarks'" :value="col.value" />

              <span v-else>{{ col.value }}</span>
            </q-td>
          </template>
          <template #expanded-row="props">
            <q-td></q-td>
            <q-td :colspan="productsTableColumns.length - 1">
              <div class="flex">
                <PTabs
                  :model-value="getActiveTab(props.rowIndex)"
                  align="left"
                  dense
                  shrink
                  @update:model-value="
                    (activeTab) => setActiveTab(activeTab, props.rowIndex)
                  "
                >
                  <PTab
                    v-for="({ disable, name, label }, index) in tabs"
                    :key="index"
                    :class="{ 'text-neutral-70': activeTab !== name }"
                    :disable="disable"
                    :name="name"
                    :label="label"
                    class="col"
                  />
                </PTabs>
              </div>
              <q-tab-panels
                :model-value="getActiveTab(props.rowIndex)"
                class="transparent"
                animated
              >
                <q-tab-panel class="q-pa-none" name="products">
                  <PMarkupTable
                    :columns="productsTableColumns"
                    :rows="props.row.lineitems"
                  />
                </q-tab-panel>
              </q-tab-panels>
            </q-td>
          </template>
        </PTable>
      </span>
    </div>

    <q-separator
      v-if="
        !qualityCheckFormState.view ||
        formData.credit_notes?.length ||
        formData.debit_notes?.length
      "
      class="q-mb-xl q-mt-md"
    />
    <div
      v-if="!qualityCheckFormState.view"
      class="border-none q-ma-none q-pa-none"
    >
      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PFile
            v-model="file"
            label="Attach Proof of Document (QC Report)"
            help="Allowed file types are PDFs, images, excel, csv"
            required
          />
          <PFile
            v-if="file"
            v-model="file"
            class="q-mt-xxs"
            variant="re-upload"
          />
        </div>
      </div>

      <q-separator class="q-my-xl" />

      <div class="row q-col-gutter-lg">
        <div class="col-12">
          <PCheckbox
            v-model="formData.gen_cn_dn"
            class="q-ml-auto"
            label="Generate Credit & Debit Notes automatically"
            size="sm"
            dense
            disable
          />
        </div>
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { date } from "quasar";

import AddIcon from "assets/icons/add.svg";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";

import qcFormModel from "src/modules/purchase-order/models/qualityCheckForm";

import { numberFormat } from "assets/scripts/utilities/helpers";
import { notify } from "assets/scripts/utilities/api-clients";
import { useDebitNoteStore } from "src/stores/debit-note";

const { formatDate } = date;

const qcStatusOptions = ref([
  {
    labelClass: "text-success-80",
    label: "Passed",
    value: "passed",
  },
  {
    labelClass: "text-warning-70",
    label: "Partially Rejected",
    value: "partially_rejected",
  },
  {
    labelClass: "text-error-70",
    label: "Rejected",
    value: "rejected",
  },
]);

const validatingNumber = ref(false);
const invoiceHasActivePod = ref(true);

const onUpdateStatus = (status_cd) => {
  unref(formData).status = unref(qcStatusOptions).find(
    (status) => status.value === status_cd,
  ).label;

  populateLineItems();

  calculateTotalTax();
};

const isRejected = computed(() => {
  return ["partially_rejected", "rejected"].includes(unref(formData).status_cd);
});

const isFullyRejected = computed(() => {
  return unref(formData).status_cd === "rejected";
});

const pForm = ref(null);

const { qualityCheck, qualityCheckFormState } = storeToRefs(
  useQualityCheckStore(),
);
const { isDuplicate } = usePodStore();
const { purchaseOrder } = storeToRefs(usePurchaseOrderStore());
const {
  getQualityChecks,
  postQualityCheck,
  putQualityCheck,
  toggleQualityCheckForm,
} = useQualityCheckStore();

const formHeading = computed(() => {
  let heading = "Quality Check Report";

  if (unref(qualityCheckFormState).view) {
    return `${heading}`;
  }

  if (unref(qualityCheckFormState).edit) {
    return `Edit ${heading}`;
  }

  return `Add ${heading}`;
});

const formSubHeading = computed(() => {
  if (unref(qualityCheckFormState).view) {
    return `For Invoice: ${unref(formData).invc_number}`;
  }

  return "";
});

let invoiceFilter = ref({});

watchEffect(() => {
  invoiceFilter.value = useFilter({
    entity: "invoice",
    options: {
      params: {
        po_id: unref(purchaseOrder)?._id,
        stages: "pod_accepted",
        status_cd: "active",
      },
    },
  });
});

const activeInvoicesWithNumber = computed(() => {
  return (
    unref(invoiceFilter.value?.entities)?.filter((invoice) => {
      return invoice.status_cd !== "cancelled" && invoice.number;
    }) || []
  );
});

const invoicesWithActivePODs = computed(() => {
  return (
    unref(activeInvoicesWithNumber)?.filter((invoice) => {
      return invoice?.pods?.find((pod) => pod?.status_cd !== "cancelled");
    }) || []
  );
});

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: qcFormModel(),
  entity: qualityCheck,
  formRef: pForm,
  isForm: qualityCheckFormState,
  post: postQualityCheck,
  put: putQualityCheck,
  toggleForm: toggleQualityCheckForm,
});

const {
  pagination,
  addItem,
  editItem,
  removeProduct,
  getTotalAmountWithoutTax,
  calculateTotalTax,
} = useProductList({
  formData,
  purchaseOrder,
});

const showAddItemButton = computed(() => {
  if (unref(isFullyRejected)) {
    return false;
  }

  return !unref(qualityCheckFormState).view;
});

const disableLineItemActionButtons = computed(() => {
  return !unref(showAddItemButton);
});

const productListColumns = computed(() => {
  const columns = [
    {
      align: "left",
      name: "name",
      label: "Name",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: "Qty Returned",
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
    {
      align: "center",
      classes: "sticky--right",
      headerClasses: "sticky--right",
      name: "action",
      label: "Action",
      hidden: unref(qualityCheckFormState).view,
    },
  ];

  return columns;
});

const invoice = ref({});
const invoiceLineItems = ref([]);

const populateLineItems = () => {
  invoiceLineItems.value = unref(invoice).lineitems;

  if (unref(formData).status_cd === "rejected") {
    unref(formData).lineitems =
      unref(invoiceLineItems)?.map((item) => {
        const { amount, amount_wt } = item;

        return {
          ...item,
          qty: 1,
          rate: amount,
          rate_wt: amount_wt,
        };
      }) || [];
  } else {
    unref(formData).lineitems = [];
  }
};

const populateFormData = (invoice_id) => {
  invoice.value = unref(unref(invoiceFilter).entities).find(
    (invoice) => invoice._id === invoice_id,
  );

  const { bill, number, po_id, tcs, vehicle_number } = unref(invoice);
  const { tds } = bill;

  const pod = unref(invoice)?.pods?.find(
    (pod) => pod.status_cd !== "cancelled",
  );

  const { _id: pod_id } = pod || {};

  formData.value = {
    ...qcFormModel()(),
    invc_id: invoice_id,
    invc_number: number,
    po_id: po_id._id || po_id,
    pod_id,
    tcs,
    tds,
    vehicle_number,
  };

  file.value = null;

  populateLineItems();
};

const onReset = () => {
  resetForm();
};

watch(isRejected, (returned) => {
  unref(formData).gen_cn_dn = returned;
});

const { getCounts, getPurchaseOrderFiles } = usePurchaseOrderStore();
const { file, uploadFile } = useFile(formData);
const { getCreditNotes } = useCreditNoteStore();
const { getDebitNotes } = useDebitNoteStore();

const onSubmit = async () => {
  try {
    const submission = await submitForm(false);

    isLoading.value = true;

    if (unref(file)) {
      await uploadFile({
        ...submission?.data?.data,
        type: "Quality Check",
        showMessage: false,
      });
    }

    await Promise.all([
      getCounts(),
      getQualityChecks(),
      getCreditNotes(),
      getDebitNotes(),
      getPurchaseOrderFiles(),
    ]);

    closeForm(true);

    notify({
      type: "positive",
      message: submission?.data?.message,
    });
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const qcHasLineItems = computed(() => {
  return (
    unref(formData).status_cd === "passed" ||
    unref(formData).lineitems.length > 0
  );
});

const submitBtn = computed(() => {
  if (unref(qualityCheckFormState).view) {
    return {};
  }

  return {
    loading: unref(isLoading),
    disable:
      unref(validatingNumber) ||
      !unref(invoiceHasActivePod) ||
      !unref(qcHasLineItems),
  };
});

const resetButton = computed(() => {
  if (unref(qualityCheckFormState).view) {
    return {};
  }

  return undefined;
});

const validateNumber = async (invoice_id) => {
  const invoice = unref(unref(invoiceFilter).entities).find(
    (invoice) => invoice._id === invoice_id,
  );

  try {
    invoiceHasActivePod.value = true;

    validatingNumber.value = true;

    const payload = {
      invc_id: invoice._id,
      invc_number: invoice.number,
      po_id: invoice.po_id,
    };

    await isDuplicate(payload);

    validatingNumber.value = false;

    invoiceHasActivePod.value = false;

    return `There is no active POD against this invoice ${invoice.number}. Please add a POD from POD Tab before adding Quality Check`;
  } catch (error) {
    validatingNumber.value = false;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      return true;
    }
  }
};

const creditNotesColumns = ref([
  {
    align: "left",
    name: "number",
    label: "Number",
    field: "number",
  },
  {
    align: "left",
    name: "date",
    label: "Date",
    field: "date",
    format: (date, cn) =>
      cn.stage === "Completed" ? formatDate(date, "DD MMM YYYY") || "-" : "-",
  },
  {
    align: "left",
    name: "status",
    label: "Status",
    field: "status",
  },
  {
    align: "left",
    name: "stage",
    label: "Stage",
    field: "stage",
  },
  {
    align: "right",
    name: "total_amount",
    label: "Total",
    field: "total_amount",
    format: (totalAmount) => {
      return numberFormat({
        number: totalAmount,
        style: "currency",
      });
    },
  },
]);

const tabs = ref([
  {
    disable: false,
    name: "products",
    label: "Products",
  },
]);

const activeTab = ref("products");

const { setActiveTab, getActiveTab } = useTabs(activeTab);

const productsTableColumns = ref([
  {
    label: "No.",
    field: "rowIndex",
    format: (value) => {
      return value + 1;
    },
  },
  {
    label: "Name",
    field: "name",
  },
  {
    label: "HSN/SAC",
    field: "hsn",
  },
  {
    label: "GST (%)",
    field: "gst",
  },
  {
    label: "CESS (%)",
    field: "cess",
  },
  {
    label: "Qty",
    field: "qty",
    format: (value, row) => `${value} ${row.unit || "Unit"}`,
  },
  {
    align: "right",
    label: "Rate (Excl. Tax)",
    field: "rate",
    format: (value) => numberFormat({ number: value, style: "currency" }),
  },
  {
    align: "right",
    label: "Rate (Incl. Tax)",
    field: "rate_wt",
    format: (value) =>
      value ? numberFormat({ number: value, style: "currency" }) : "-",
  },
  {
    align: "right",
    label: "Amount (Excl. Tax)",
    field: "amount",
    format: (value) => numberFormat({ number: value, style: "currency" }),
  },
  {
    align: "right",
    label: "Amount (Incl. Tax)",
    field: "amount_wt",
    format: (value) =>
      value ? numberFormat({ number: value, style: "currency" }) : "-",
  },
]);

const { setCreditNote, toggleCreditNoteForm } = useCreditNoteStore();

const { setDebitNote, toggleDebitNoteForm } = useDebitNoteStore();

const previewCreditNote = (creditNote) => {
  setCreditNote(creditNote);

  toggleCreditNoteForm({
    level: 1,
    edit: true,
    open: true,
    preview: true,
  });
};

const previewDebitNote = (debitNote) => {
  setDebitNote(debitNote);

  toggleDebitNoteForm({
    level: 1,
    edit: true,
    open: true,
    preview: true,
  });
};

const associatedCreditDebitNotes = [
  {
    rowLabel: "Credit Notes",
    key: "credit_notes",
    cta: previewCreditNote,
  },
  {
    rowLabel: "Debit Notes",
    key: "debit_notes",
    cta: previewDebitNote,
  },
];

const hasCreditNotesDebitNotes = computed(() => {
  return (
    unref(formData).credit_notes?.length || unref(formData).debit_notes?.length
  );
});
</script>
